(() => {
  'use strict';
  if (window.__nsCartLanguageReady) return;
  const configNode = document.getElementById('NSCartLanguage');
  if (!configNode) return;
  let config;
  try { config = JSON.parse(configNode.textContent); } catch (_) { return; }
  const locale = String(config.locale || '').toLowerCase().split('-')[0];
  if (!['es', 'en'].includes(locale) || !config.labels) return;
  window.__nsCartLanguageReady = true;

  const rootSelector = '.ocu-cart-drawer, section.ocu-widget';
  const labels = config.labels;
  const drawers = new Map();
  const pending = new Set();
  const titles = new Map();
  const ambiguous = new Set();
  let discovery;
  let scheduled = false;
  let stopped = false;

  for (const pair of config.titles || []) {
    if (!Array.isArray(pair) || pair.length !== 2 || pair.some(value => typeof value !== 'string')) continue;
    const [from, to] = locale === 'es' ? [pair[1], pair[0]] : pair;
    if (from === to) continue;
    if (titles.has(from) && titles.get(from) !== to) ambiguous.add(from);
    titles.set(from, to);
  }
  for (const from of ambiguous) titles.delete(from);
  for (const [from, to] of [...titles]) {
    if (titles.has(to) && titles.get(to) !== to) titles.delete(from);
  }

  function label(key) {
    const value = labels[key];
    return typeof value === 'string' && value && !/translation missing/i.test(value) ? value : null;
  }

  function exact(value, sources, key) {
    return sources.includes(value) ? label(key) || value : value;
  }

  function template(value, patterns, key, token) {
    const output = label(key);
    if (!output || !output.includes(token)) return value;
    const [prefix, suffix] = output.split(token);
    if (value.startsWith(prefix) && value.endsWith(suffix) && value.length > prefix.length + suffix.length) return value;
    for (const pattern of patterns) {
      const match = value.match(pattern);
      if (match) return output.replace(token, match[1]);
    }
    return value;
  }

  function textNodes(container, transform) {
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    let node;
    while ((node = walker.nextNode())) {
      if (node.parentElement?.closest('script, style, textarea, select, option, template, noscript')) continue;
      const original = node.nodeValue;
      const trimmed = original.trim();
      if (!trimmed) continue;
      const next = transform(trimmed);
      if (next !== trimmed) node.nodeValue = original.slice(0, original.indexOf(trimmed)) + next + original.slice(original.indexOf(trimmed) + trimmed.length);
    }
  }

  function textRule(root, selector, transform) {
    root.querySelectorAll(selector).forEach(node => textNodes(node, transform));
  }

  function attribute(node, name, transform) {
    const original = node.getAttribute(name);
    if (original === null) return;
    const next = transform(original);
    if (next !== original) node.setAttribute(name, next);
  }

  // Display-only translations for reviewed offer option sets. An unknown ID,
  // changed label, or changed option count leaves the whole selector untouched.
  function localizeVariantSelects(root) {
    if (locale !== 'es') return;
    const rules = [
      {
        prompts: ['Choose the MagicMag flavor®', 'Escoja el sabor de MagicMag®'],
        flavors: {
          '46321069457492': ['Strawberry-Lime', 'Fresa-Lima'],
          '46321069490260': ['Raspberry-Lemon', 'Frambuesa-Limón'],
          '46321069523028': ['Chamomile-Honey', 'Manzanilla-Miel']
        }
      },
      {
        prompts: ['Metabolic Protein Flavor #1/Metabolic Protein Flavor #2/Metabolic Protein Flavor #3', 'Elija los sabores de sus 3 batidas'],
        flavors: {
          "39692535103572": ["Vanilla / Vanilla / Vanilla", "Vainilla / Vainilla / Vainilla"],
          "39692535136340": ["Vanilla / Vanilla / Chocolate", "Vainilla / Vainilla / Chocolate"],
          "39692535169108": ["Vanilla / Vanilla / Strawberry", "Vainilla / Vainilla / Fresa"],
          "39692535201876": ["Vanilla / Chocolate / Vanilla", "Vainilla / Chocolate / Vainilla"],
          "39692535234644": ["Vanilla / Chocolate / Chocolate", "Vainilla / Chocolate / Chocolate"],
          "39692535267412": ["Vanilla / Chocolate / Strawberry", "Vainilla / Chocolate / Fresa"],
          "39692535300180": ["Vanilla / Strawberry / Vanilla", "Vainilla / Fresa / Vainilla"],
          "39692535332948": ["Vanilla / Strawberry / Chocolate", "Vainilla / Fresa / Chocolate"],
          "39692535365716": ["Vanilla / Strawberry / Strawberry", "Vainilla / Fresa / Fresa"],
          "39692535398484": ["Strawberry / Vanilla / Vanilla", "Fresa / Vainilla / Vainilla"],
          "39692535431252": ["Strawberry / Vanilla / Chocolate", "Fresa / Vainilla / Chocolate"],
          "39692535464020": ["Strawberry / Vanilla / Strawberry", "Fresa / Vainilla / Fresa"],
          "39692535496788": ["Strawberry / Chocolate / Vanilla", "Fresa / Chocolate / Vainilla"],
          "39692535529556": ["Strawberry / Chocolate / Chocolate", "Fresa / Chocolate / Chocolate"],
          "39692535562324": ["Strawberry / Chocolate / Strawberry", "Fresa / Chocolate / Fresa"],
          "39692535595092": ["Strawberry / Strawberry / Vanilla", "Fresa / Fresa / Vainilla"],
          "39692535627860": ["Strawberry / Strawberry / Chocolate", "Fresa / Fresa / Chocolate"],
          "39692535660628": ["Strawberry / Strawberry / Strawberry", "Fresa / Fresa / Fresa"],
          "39692535693396": ["Chocolate / Vanilla / Vanilla", "Chocolate / Vainilla / Vainilla"],
          "39692535726164": ["Chocolate / Vanilla / Chocolate", "Chocolate / Vainilla / Chocolate"],
          "39692535758932": ["Chocolate / Vanilla / Strawberry", "Chocolate / Vainilla / Fresa"],
          "39692535791700": ["Chocolate / Chocolate / Vanilla", "Chocolate / Chocolate / Vainilla"],
          "39692535824468": ["Chocolate / Chocolate / Chocolate", "Chocolate / Chocolate / Chocolate"],
          "39692535857236": ["Chocolate / Chocolate / Strawberry", "Chocolate / Chocolate / Fresa"],
          "39692535890004": ["Chocolate / Strawberry / Vanilla", "Chocolate / Fresa / Vainilla"],
          "39692535922772": ["Chocolate / Strawberry / Chocolate", "Chocolate / Fresa / Chocolate"],
          "39692535955540": ["Chocolate / Strawberry / Strawberry", "Chocolate / Fresa / Fresa"]
        }
      }
    ];
    root.querySelectorAll('select.ocu-variant__dropdown').forEach(select => {
      const options = [...select.querySelectorAll('option')];
      const prompt = options.find(option => option.getAttribute('value') === 'product-option-title');
      if (!prompt || prompt.getAttribute('disabled') === null) return;
      const rule = rules.find(candidate => candidate.prompts.includes(prompt.textContent.trim()));
      if (!rule || options.length !== Object.keys(rule.flavors).length + 1) return;
      const choices = options.filter(option => option !== prompt);
      if (new Set(choices.map(option => option.getAttribute('value'))).size !== choices.length) return;
      if (choices.some(option => {
        const pair = rule.flavors[option.getAttribute('value')];
        return !Array.isArray(pair) || !pair.includes(option.textContent.trim());
      })) return;
      if (options.some(option => option.childNodes.length !== 1 || option.firstChild.nodeType !== 3)) return;
      options.forEach(option => {
        const original = option.firstChild.nodeValue;
        const next = option === prompt ? rule.prompts[1] : rule.flavors[option.getAttribute('value')][1];
        if (original.trim() !== next) option.firstChild.nodeValue = original.replace(original.trim(), next);
      });
      attribute(select, 'aria-label', value => value === 'Select ' + rule.prompts[0] ? rule.prompts[1] : value);
    });
  }

  function localizeSelectedOptions(root) {
    if (locale !== 'es') return;
    const choices = {
      '3 Strawberry C Plus': '3 Fresa C Plus',
      '3 Strawberry Collagen': '3 Fresa Colágeno',
      '3 Chocolate C Plus': '3 Chocolate C Plus',
      '3 Chocolate Collagen': '3 Chocolate Colágeno',
      '3 Vanilla C Plus': '3 Vainilla C Plus',
      '3 Vanilla Collagen': '3 Vainilla Colágeno',
      '1 Strawberry + 1 Vanilla + 1 Chocolate Collagen': '1 Fresa + 1 Vainilla + 1 Chocolate Colágeno',
      '1 Strawberry + 1 Vanilla + 1 Chocolate C Plus': '1 Fresa + 1 Vainilla + 1 Chocolate C Plus'
    };
    const flavors = { Strawberry: 'Fresa', Fresa: 'Fresa', Vanilla: 'Vainilla', Vainilla: 'Vainilla', Chocolate: 'Chocolate' };
    const kitChoice = value => {
      const parts = value.split(' / ');
      return parts.length === 3 && parts.every(part => typeof flavors[part] === 'string')
        ? parts.map(part => flavors[part]).join(' / ') : value;
    };
    const programChoice = value => {
      const separator = value.indexOf(' / ');
      if (separator < 0) return value;
      const translated = choices[value.slice(0, separator)];
      return typeof translated === 'string' ? translated + value.slice(separator) : value;
    };
    root.querySelectorAll('.ocu-cart-line-item-product').forEach(product => {
      const title = product.querySelectorAll('.ocu-cart-line-item-product-title')[0];
      if (!title) return;
      const href = title.getAttribute('href') || '';
      const program = /^\/(?:es\/)?products\/relaxslim-program-free-shipping(?:[?#]|$)/.test(href);
      const kit = /^\/(?:es\/)?products\/kit-super-batida-naturalslim(?:®|%[cC]2%[aA][eE])(?:[?#]|$)/.test(href);
      if (!program && !kit) return;
      textRule(product, '.ocu-cart-line-item-product-variant', value => {
        if (kit) {
          const match = value.match(/^Metabolic Protein (?:Sabor|Flavor) #([123]): (Fresa|Strawberry|Vanilla|Vainilla|Chocolate)(,?)$/);
          return match ? 'Batida ' + match[1] + ': ' + flavors[match[2]] + match[3] : value;
        }
        const match = value.match(/^(?:Elija su sabor|Elige tu sabor|Metabolic Protein Flavor): (.+?)(,?)$/);
        const translated = match && choices[match[1]];
        return typeof translated === 'string' ? 'Elija su sabor: ' + translated + match[2] : value;
      });
      const displayVariant = program ? programChoice : kitChoice;
      const prefix = title.textContent.trim() + ' - ';
      const accessibleVariant = value => value.startsWith(prefix)
        ? prefix + displayVariant(value.slice(prefix.length)) : displayVariant(value);
      attribute(title, 'aria-label', accessibleVariant);
      attribute(title, 'title', accessibleVariant);
      const line = product.closest('.ocu-cart-line-item');
      if (!line) return;
      line.querySelectorAll('.ocu-cart-line-item-image').forEach(link => {
        // The image and title must point at the exact same selected item.
        if (link.getAttribute('href') !== href) return;
        attribute(link, 'aria-label', accessibleVariant);
        attribute(link, 'title', accessibleVariant);
        link.querySelectorAll('img').forEach(image => attribute(image, 'alt', accessibleVariant));
      });
    });
  }

  function apply(root) {
    localizeVariantSelects(root);
    localizeSelectedOptions(root);
    if (root.matches('section.ocu-widget')) {
      root.querySelectorAll('.ocu-product-card__title .ocu-name').forEach(node => {
        textNodes(node, value => titles.get(value) || value);
        attribute(node, 'title', value => titles.get(value) || value);
      });
      return;
    }
    attribute(root, 'aria-label', value => exact(value, ['Your Cart', 'Your cart', 'Su carrito'], 'cart'));
    root.querySelectorAll('.ocu-cart-header--close').forEach(node => attribute(node, 'aria-label', value => exact(value, ['Close', 'Close cart', 'Cerrar', 'Cerrar el carrito'], 'close')));
    const rules = [
      ['.ocu-cart-header--title-text', ['En su carrito -', 'In your cart -', 'Your cart -', 'Your Cart', 'Your cart'], 'cart_title'],
      ['.ocu-empty-cart__item', ['Your cart is empty', 'Su carrito está vacío'], 'empty'],
      ['.ocu-continue-shopping', ['Continuar Comprando', 'Continuar comprando', 'Siga comprando', 'Continue shopping', 'Continue Shopping'], 'continue_shopping'],
      ['.ocu-quantity > .ocu-visually-hidden', ['Quantity', 'Cantidad'], 'quantity'],
      ['.ocu-checkout-button-text', ['Complete su orden', 'Complete your order', 'Checkout', 'Check out', 'Continuar al pago', 'Continue to checkout'], 'checkout'],
      ['.ocu-cart-footer', ['Subtotal', 'Sub-total'], 'subtotal'],
      ['.ocu-cart-line-price-text, .ocu-savings-block .ocu-price--width', ['Savings', 'Ahorro', 'Ahorros'], 'savings'],
      ['.ocu-discount-info', ['Descuento', 'Discount'], 'discount'],
      ['.ocu-shipping-info', ['Envío', 'Shipping'], 'shipping'],
      ['.ocu-shipping-info', ['Gratis', 'Free'], 'shipping_at_checkout'],
      ['.ocu-estimated-info .ocu-editable-cart-text', ['Total estimado', 'Estimated total', 'Estimated Total'], 'estimated_total'],
      ['.ocu-title-embedded', ['También podría gustarle', 'También podría interesarle', 'You may also like', 'You May Also Like'], 'recommendations'],
      ['.ocu-product-add__button', ['Agregar', 'Añadir', 'Añada', 'Add', 'Add to cart'], 'add'],
      ['.ocu-cart-banner--message-text', ['Formulaciones por el Especialista Frank Suárez', 'Formulations by Specialist Frank Suárez', 'NaturalSlim®, fundada por Frank Suárez', 'NaturalSlim®, founded by Frank Suárez'], 'banner'],
      ['.ocu-reward-bar__title', ['Hoy, todas las órdenes califican para ¡envío gratuito!', '¡Su orden tiene envío gratis!', 'Su pedido tiene envío gratis.', 'Your order has free shipping.'], 'shipping_at_checkout']
    ];
    rules.forEach(([selector, sources, key]) => textRule(root, selector, value => exact(value, sources, key)));
    // OCU's $10 reward threshold is not proof of checkout shipping eligibility.
    // Replace only its reviewed shipping sentence; never infer or change rates.
    textRule(root, '.ocu-shipping-info', value =>
      /^Compre \$(?:\d+|\d{1,3}(?:,\d{3})+)\.\d{2} para envío gratis$/.test(value)
        ? label('shipping_at_checkout') || value : value);
    textRule(root, '.ocu-cart-header--title > .ocu-visually-hidden', value => {
      const match = value.match(/^(\d+) (?:items?|artículos?)$/);
      if (!match) return value;
      return (label(match[1] === '1' ? 'item_one' : 'item_other') || value).replace('__COUNT__', match[1]);
    });
    const decrease = value => template(value, [/^Decrease (.+)$/, /^Disminuir (.+)$/], 'decrease_item', '__PRODUCT__');
    const increase = value => template(value, [/^Increase (.+)$/, /^Aumentar (.+)$/], 'increase_item', '__PRODUCT__');
    textRule(root, '.ocu-quantity-minus .ocu-visually-hidden', decrease);
    textRule(root, '.ocu-quantity-plus .ocu-visually-hidden', increase);
    root.querySelectorAll('.ocu-quantity-input').forEach(node => attribute(node, 'aria-label', value => template(value, [/^Quantity for (.+)$/], 'quantity_for', '__PRODUCT__')));
    root.querySelectorAll('.ocu-cart-line-item-remove').forEach(node => attribute(node, 'aria-label', value => template(value, [/^Remove (.+)$/], 'remove_item', '__PRODUCT__')));
    root.querySelectorAll('.ocu-carousel-indicators__slide').forEach(node => attribute(node, 'aria-label', value => {
      if (value === 'Current slide' || value === 'Diapositiva actual') return label('current_slide') || value;
      return template(value, [/^Go to slide (\d+)$/, /^Vaya a la diapositiva (\d+)$/], 'go_to_slide', '__COUNT__');
    }));
    root.querySelectorAll('.ocu-cart-line-item-product-title, .ocu-widget__title-wrapper').forEach(node => {
      textNodes(node, value => titles.get(value) || value);
      attribute(node, 'title', value => titles.get(value) || value);
    });
  }

  function schedule(root) {
    if (stopped) return;
    pending.add(root);
    if (scheduled) return;
    scheduled = true;
    queueMicrotask(() => {
      scheduled = false;
      if (stopped) return;
      for (const drawer of pending) {
        const observer = drawers.get(drawer);
        if (!observer || !drawer.isConnected) continue;
        observer.disconnect();
        apply(drawer);
        observer.observe(drawer, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['aria-label', 'title', 'alt'] });
      }
      pending.clear();
    });
  }

  function discover(node) {
    if (node.nodeType !== 1) return;
    const found = node.matches(rootSelector) ? [node] : node.querySelectorAll(rootSelector);
    found.forEach(root => {
      if (drawers.has(root)) return;
      const observer = new MutationObserver(() => schedule(root));
      drawers.set(root, observer);
      schedule(root);
    });
  }

  function start() {
    if (!document.body) return;
    stopped = false;
    discover(document.body);
    if (discovery) return;
    discovery = new MutationObserver(records => {
      for (const [root, observer] of drawers) {
        if (!root.isConnected) { observer.disconnect(); drawers.delete(root); pending.delete(root); }
      }
      for (const record of records) {
        if (record.target.nodeType === 1 && record.target.closest(rootSelector)) continue;
        record.addedNodes.forEach(discover);
      }
    });
    discovery.observe(document.body, { childList: true, subtree: true });
  }

  function stop() {
    stopped = true;
    discovery?.disconnect();
    discovery = null;
    drawers.forEach(observer => observer.disconnect());
    drawers.clear();
    pending.clear();
  }

  window.addEventListener('pagehide', stop);
  window.addEventListener('pageshow', event => { if (event.persisted) start(); });
  if (document.body) start();
  else document.addEventListener('DOMContentLoaded', start, { once: true });
})();
